import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { logError } from "@/lib/logger";

const StoreCtx = createContext(null);

export function StoreProvider({ children }) {
  const { user } = useAuth();
  const [stores, setStores] = useState([]);
  const [activeStore, setActiveStore] = useState(() => localStorage.getItem("tecassist_store") || "");
  const [loading, setLoading] = useState(false);

  const refresh = React.useCallback(async () => {
    if (!user || (user.role !== "admin" && user.role !== "superadmin")) return;
    setLoading(true);
    try {
      const { data } = await api.get("/admin/stores");
      setStores(data);
      if (user.role !== "superadmin" && data[0]) setActiveStore(data[0].store_id);
    } catch (err) {
      logError("[stores] fetch failed:", err);
    } finally { setLoading(false); }
  }, [user]);

  useEffect(() => { refresh(); }, [user?.user_id, refresh]);

  const pick = (id) => {
    setActiveStore(id || "");
    if (id) localStorage.setItem("tecassist_store", id);
    else localStorage.removeItem("tecassist_store");
  };

  // Superadmin: active can be "" (all stores). Regular admin: locked.
  const effectiveStoreId = user?.role === "superadmin" ? activeStore : user?.store_id;
  const activeStoreObj = stores.find((s) => s.store_id === effectiveStoreId) || null;

  const value = useMemo(
    () => ({ stores, activeStore: effectiveStoreId, activeStoreObj, pick, refresh, loading }),
    [stores, effectiveStoreId, activeStoreObj, refresh, loading],
  );

  return <StoreCtx.Provider value={value}>{children}</StoreCtx.Provider>;
}

export const useStores = () => useContext(StoreCtx);
export const storeQS = (id) => (id ? `?store_id=${encodeURIComponent(id)}` : "");
